import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Avatar,
} from "@mui/material";
import { CircularOverlay } from "../ui";
import { ROUTES } from "../../constants/routes";
import { User } from "../../types/frontend";
import { getStaticAssetPath } from "../../utils/staticAssets";

interface UserDropdownMenuProps {
  handleLogout: () => void;
  user: User | null;
}

/**
 * UserDropdownMenu Component
 *
 * Handles the desktop user profile widget and dropdown menu.
 * Shows user avatar with circular overlay and dropdown menu with profile options.
 */
const UserDropdownMenu: React.FC<UserDropdownMenuProps> = ({
  handleLogout,
  user,
}) => {
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const navigate = useNavigate();

  /**
   * Opens the user menu dropdown.
   * @param event - The mouse event from clicking the icon button.
   */
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  /**
   * Closes the user menu dropdown.
   */
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  /**
   * Handles the click event for viewing the user's profile.
   * Currently logs to console and closes the menu.
   */
  const handleProfileClick = () => {
    // Navigate to user profile using the user's unique ID (`uid`)

    if (user?._id) {
      navigate(`${ROUTES.USER_DIRECTORY}/${user._id}`);
    }
    setUserMenuAnchor(null);
  };

  /**
   * Handles the click event for logging out the user.
   * Calls the provided handleLogout prop and closes the menu.
   */
  const handleLogoutClick = () => {
    handleLogout();
    setUserMenuAnchor(null);
  };

  /**
   * Handles the click event for updating user details.
   * Currently logs to console and closes the menu.
   */
  const handleUpdateDetailsClick = () => {
    navigate(ROUTES.UPDATE_DETAILS); // Navigate to Update Details page
    setUserMenuAnchor(null);
  };

  /**
   * Returns the avatar image path for the user, or an empty string if not available.
   * @param user - The user object or null.
   * @returns The static asset path for the user's avatar image.
   */
  const getUserAvatar = (user: User | null) => {
    if (user) {
      return getStaticAssetPath(user.image || "");
    }
    return "";
  };

  /**
   * Returns the user's name, or an empty string if not available.
   * @param user - The user object or null.
   * @returns The user's name as a string.
   */
  const getUserName = (user: User | null) => {
    if (user) {
      return user.name || "";
    }
    return "";
  };

  return (
    <>
      {/* User Profile Widget */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton
          onClick={handleUserMenuOpen}
          disableRipple
          sx={{
            p: 0.625, // 5px padding like in the example
            position: "relative",
          }}
        >
          {/* SVG Circle Overlay */}
          <CircularOverlay />

          {/* Profile Avatar - Use placeholder for now */}
          <Avatar src={getUserAvatar(user)} alt={getUserName(user)} />
        </IconButton>
      </Box>

      {/* User Profile Dropdown Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        elevation={1}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          mt: 1,
          "& .MuiPaper-root": {
            minWidth: 180,
            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <MenuItem onClick={handleProfileClick}>View your profile</MenuItem>
        <MenuItem onClick={handleUpdateDetailsClick}>Update details</MenuItem>
        <Divider />
        <MenuItem onClick={handleLogoutClick}>Log Out</MenuItem>
      </Menu>
    </>
  );
};

export default UserDropdownMenu;
